import axios, { AxiosInstance } from "axios";
import { BASE_URL } from "../../const";

export interface PartnerCompany {
  tenant_id: string;
  name: string;
  created_at: string;
}

export interface PartnerContext {
  partner_id: number;
  name: string;
}

type PartnerApiClient = Pick<AxiosInstance, "get" | "post">;

const PARTNER_LOGIN_AS_USERNAME_PATTERN =
  /^partner\+(.+)@partner\.asktally\.com$/;

export function getPartnerToken() {
  return localStorage.getItem("partnerToken") || localStorage.getItem("token");
}

export function hasPartnerSession() {
  return Boolean(localStorage.getItem("partnerToken"));
}

export function getCurrentTenantId(): string | null {
  const token = localStorage.getItem("token");
  if (!token) {
    return null;
  }

  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    const sub: string = payload.sub ?? "";
    const match = sub.match(PARTNER_LOGIN_AS_USERNAME_PATTERN);
    return match ? match[1] : null;
  } catch {
    return null;
  }
}

export function createPartnerApiClient(): PartnerApiClient {
  const token = getPartnerToken();

  return axios.create({
    baseURL: BASE_URL,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
}

export async function fetchPartnerCompanies(
  client: PartnerApiClient,
): Promise<PartnerCompany[]> {
  const response = await client.get("/v1/partner/companies/");
  return (response.data as PartnerCompany[] | undefined) ?? [];
}

export async function fetchPartnerContext(
  client: PartnerApiClient,
): Promise<PartnerContext | null> {
  const response = await client.get("/v1/partner/me/");
  return (response.data as PartnerContext | undefined) ?? null;
}

export async function fetchPartnerLoginAsUrl(
  client: PartnerApiClient,
  tenantId: string,
): Promise<string | null> {
  const response = await client.get(
    `/v1/partner/companies/${tenantId}/login-as/`,
  );
  return response.data?.url ?? null;
}

export async function createPartnerCompany(
  companyName: string,
  termsAndConditionsHash: string | null,
  dataProtectionPolicyHash: string | null,
) {
  const client = createPartnerApiClient();
  return client.post("/v1/partner/companies/", {
    company_name: companyName,
    terms_and_conditions_hash: termsAndConditionsHash,
    data_protection_policy_hash: dataProtectionPolicyHash,
  });
}

export function restorePartnerSession() {
  const partnerToken = localStorage.getItem("partnerToken");
  if (!partnerToken) {
    return false;
  }

  localStorage.setItem("token", partnerToken);
  return true;
}

export function storePartnerSession() {
  const currentToken = localStorage.getItem("token");
  if (!localStorage.getItem("partnerToken") && currentToken) {
    localStorage.setItem("partnerToken", currentToken);
  }
}

export function applyPartnerLoginUrl(url: string) {
  const token = new URL(url).searchParams.get("token");
  if (!token) {
    return false;
  }

  storePartnerSession();
  localStorage.setItem("token", token);
  return true;
}
